import { Button, CircularProgress, Typography } from '@material-ui/core';
import { acceptCourseInvite, checkInvite } from 'actionCreators/invite';
import { pushMessage } from 'actionCreators/messages';
import { WithRouter } from 'decorators/withRouter';
import { WithStyles } from 'decorators/withStyles';
import React, { Fragment, PureComponent, SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { fetchUserProfile } from 'actionCreators/user';

import { IInviteState } from 'reducers/invite';
import Login from 'components/Login';
import { IUser } from 'interface/user';
import { logout } from 'actionCreators/auth';

interface IResponseMessage {
  message?: JSX.Element;
}

interface IProps {
  acceptCourseInvite: typeof acceptCourseInvite;
  pushMessage: typeof pushMessage;
  checkInvite: typeof checkInvite;
  match: any;
  isAuthenticated?: boolean;
  classes?: any;
  baseUrl: string;
  fetchUserProfile: typeof fetchUserProfile;
  user?: IUser;
  logout: typeof logout;
  history?: any;
  invite?: IInviteState;
}

@WithRouter()
@WithStyles(() => ({
  loginMessage: {
    color: '#616161',
    textAlign: 'center',
    padding: 44,
    display: 'flex',
    flexDirection: 'column',
    height: 600,
    fontSize: 24,
  },
  loader: {
    textAlign: 'center',
    '& h3': {
      marginTop: 10,
      marginBottom: 20,
    },
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  bigLoader: {
    marginTop: 114,
    '& > div': {
      minHeight: 80,
      minWidth: 80,
    },
  },
  buttonSubmit: {
    padding: '12px 30px',
    borderRadius: 30,
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 400,
  },
}))
class Invite extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props);
    this.state = {};
  }

  async componentDidMount() {
    if (!this.props.isAuthenticated) {
      await this.checkIsValidInvite();
      return
    };

    await this.props.fetchUserProfile();
    await this.acceptCourseInvite();
  }

  async componentDidUpdate(oldProps: IProps) {
    if (!oldProps.isAuthenticated && this.props.isAuthenticated) {
      await this.props.fetchUserProfile();
      await this.acceptCourseInvite();
    }

    const { courseHash } = this.props.match.params;

    if (this.props.invite.successStatus) {
      this.props.history.push(`/${courseHash}`);
    }
  }

  checkIsValidInvite = async () => {
    const { id: hash } = this.props.match.params;

    await this.props.checkInvite(hash);
  };

  acceptCourseInvite = async () => {
    const { courseHash, id } = this.props.match.params;

    if (id) {
      await this.props.acceptCourseInvite(courseHash, id);
    }
  };

  handleLogout = async (e: SyntheticEvent) => {
    const { match } = this.props;
    const urlParams = new URLSearchParams(window.location.search);
    const accounts_invitation = urlParams.get('accounts_invitation');

    if (!!e) {
      e.preventDefault();
      e.stopPropagation();
    }

    await this.props.logout(
      `/convite/${match.params.courseHash}/${match.params.id}${!!accounts_invitation
        ? `?accounts_invitation=${accounts_invitation}`
        : ''
      }`
    );
  };

  getMessage = (): IResponseMessage => {
    const { user, invite } = this.props;

    switch (invite?.errorStatus) {
      case 403:
        return {
          message: (
            <div>
              <Typography variant='subtitle1'>
                Esse convite não pertence ao usuário que você está logado
              </Typography>

              <Typography variant='body1'>{!!user && user.email}</Typography>

              <Typography variant='subtitle1'>
                Por favor troque de usuário e tente novamente.
              </Typography>
            </div>
          ),
        };

      case 404:
        return {
          message: (
            <div>
              <Typography variant='subtitle1'>
                Esse convite não está mais válido.
              </Typography>

              <Typography variant='subtitle1'>
                Por favor, solicite um novo convite ao seu produtor.
              </Typography>
            </div>
          ),
        };

      default:
        return {
          message: (
            <Fragment>
              <Typography variant='subtitle1'>
                Ops. Parece que houve algum erro ao tentar ativar o convite
              </Typography>
            </Fragment>
          ),
        };
    }
  };

  render() {
    const { classes, match, isAuthenticated, baseUrl, invite } =
      this.props;

    if (!invite?.inviteStatus && !invite?.errorStatus && !isAuthenticated) {
      return (
        <div className={classes.loader}>
          <CircularProgress color='secondary' />
          <Typography variant='subtitle1'>Validando convite</Typography>
        </div>
      );
    }

    if (!isAuthenticated && invite?.inviteStatus) {
      return (
        <Fragment>
          <div className={classes.loginMessage}>
            <span>Você será redirecionado para a tela de Login</span>
            <div className={classes.bigLoader}>
              <CircularProgress color='secondary' />
            </div>
          </div>

          <Login />
        </Fragment>
      );
    }

    if (!invite?.errorStatus && !invite?.successStatus && isAuthenticated) {
      return (
        <div className={classes.loader}>
          <CircularProgress color='secondary' />
          <Typography variant='subtitle1'>Ativando convite</Typography>
        </div>
      )
    }

    if (invite?.successStatus) {
      return (
        <div className={classes.loader}>
          <Typography variant='subtitle1'>
            Parabéns. Convite foi ativado com sucesso!
          </Typography>

          <Button
            href={`${baseUrl}/curso/${match.params.courseHash}`}
            color='secondary'
            variant='contained'
          >
            Acessar
          </Button>
        </div>
      )
    }

    const { message } = this.getMessage();

    return (
      <Fragment>
        <div className={classes.loader}>
          {message}

          {invite?.errorStatus === 403 && (
            <Button
              className={classes.buttonSubmit}
              onClick={this.handleLogout}
              color='secondary'
              variant='contained'
              >
              Trocar de Usuário
            </Button>
          )}
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: any, ownProps: any) => ({
  ...ownProps,
  isAuthenticated: state.auth.isAuthenticated,
  baseUrl: state.template.base.subdomain ? '' : '/v3',
  user: state.user,
  invite: state.invite,
});

export default connect(mapStateToProps, {
  acceptCourseInvite,
  pushMessage,
  fetchUserProfile,
  logout,
  checkInvite,
})(Invite);
